import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { Download, Visibility, Description, Info } from '@mui/icons-material';

const PreviewStep = ({
  columnMapping,
  sheetData,
  sheetColumns,
  generateSampleFile,
  generatePreviewFile,
}) => {
  // ✅ Only mapped columns
  const mappedColumns = columnMapping.filter((m) => m.sheetColumn);

  // ✅ Build preview rows using correct sheetColumns index
  const previewRows = useMemo(() => {
    const allRows = sheetData.map((row) => {
      const mappedRow = {};
      mappedColumns.forEach((mapping) => {
        const colIndex = sheetColumns.indexOf(mapping.sheetColumn);
        const cellValue = colIndex >= 0 ? row[colIndex] : null;
        mappedRow[mapping.parameter] =
          cellValue !== null && cellValue !== undefined && String(cellValue).trim() !== ''
            ? cellValue
            : null;
      });
      return mappedRow;
    });

    // ✅ Filter out rows where any Required/Not Empty column is blank
    const filteredRows = allRows.filter((row) => {
      return mappedColumns.every((mapping) => {
        const isRequired =
          mapping.condition === 'Required' ||
          mapping.condition === 'Not Empty' ||
          mapping.dataType === 'phone_number'; // phone always required

        if (!isRequired) return true; // optional column, skip check

        const value = row[mapping.parameter];
        return value !== null && value !== undefined && String(value).trim() !== '';
      });
    });

    return filteredRows;
  }, [sheetData, mappedColumns, sheetColumns]);

  // ✅ Check if a cell should be highlighted (empty in required column)
  const isCellEmpty = (value) =>
    value === null || value === undefined || String(value).trim() === '';

  const isRequired = (mapping) =>
    mapping.condition === 'Required' || mapping.condition === 'Not Empty';

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Preview & Download Files
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Review your column mapping and download the sample file with instructions.
      </Alert>

      <Grid container spacing={3}>
        {/* Download Sample File Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Description sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Download Sample File
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Excel file with 3 dummy rows & README sheet
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<Download />}
                fullWidth
                onClick={generateSampleFile}
              >
                Download Sample File
              </Button>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Includes:</strong>
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • Sample data with 3 rows
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • README with detailed instructions
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • Column structure as per mapping
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Download Preview File Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Visibility sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Download Preview File
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Preview your mapped data (first 100 rows)
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<Download />}
                fullWidth
                onClick={generatePreviewFile}
              >
                Download Preview File
              </Button>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Includes:</strong>
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • Your actual data (first 100 rows)
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • Column mapping summary
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  • Data validation report
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Column Mapping Summary */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Column Mapping Summary
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Template Parameter</TableCell>
                <TableCell>Sheet Column</TableCell>
                <TableCell>Column Index</TableCell>
                <TableCell>Data Type</TableCell>
                <TableCell>Condition</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedColumns.map((mapping, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {mapping.parameter}
                    </Typography>
                  </TableCell>
                  <TableCell>{mapping.sheetColumn}</TableCell>
                  <TableCell>
                    <Chip label={mapping.columnIndex} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip label={mapping.dataType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mapping.condition || 'None'}
                      size="small"
                      color={isRequired(mapping) ? 'warning' : 'default'}
                      variant={isRequired(mapping) ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ✅ Data Preview - mapped columns only, correct data */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Info sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6">
              Data Preview ({previewRows.length} Valid Rows)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={`${mappedColumns.length} columns mapped`} color="primary" size="small" />
            <Chip label={`${sheetData.length} total rows`} color="default" size="small" />
          </Box>
        </Box>

        {mappedColumns.length === 0 ? (
          <Alert severity="warning">
            No columns mapped yet. Please go back and map at least one column.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 500, overflow: 'auto' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'grey.200',
                      minWidth: 50,
                    }}
                  >
                    #
                  </TableCell>
                  {mappedColumns.map((mapping, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 700,
                        bgcolor: 'grey.200',
                        minWidth: 130,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {mapping.parameter}
                      <br />
                      <Typography variant="caption" color="text.secondary" fontWeight={400}>
                        ← {mapping.sheetColumn}
                      </Typography>
                      {isRequired(mapping) && (
                        <Chip
                          label="required"
                          size="small"
                          color="warning"
                          sx={{ ml: 0.5, fontSize: '9px', height: 16 }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, rowIndex) => (
                  <TableRow key={rowIndex} hover>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      {rowIndex + 1}
                    </TableCell>
                    {mappedColumns.map((mapping, colIndex) => {
                      const value = row[mapping.parameter];
                      const empty = isCellEmpty(value);
                      const required = isRequired(mapping);

                      return (
                        <TableCell
                          key={colIndex}
                          sx={{
                            // ✅ Red tint if required field is empty
                            bgcolor: empty && required ? '#fff3f3' : 'transparent',
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: empty
                                ? required
                                  ? 'error.main'
                                  : 'text.disabled'
                                : 'text.primary',
                              fontStyle: empty ? 'italic' : 'normal',
                            }}
                          >
                            {empty ? (required ? '✕ empty' : '—') : String(value)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {previewRows.length} of {sheetData.length} total rows •{' '}
          <span style={{ color: '#d32f2f' }}>✕ empty</span> = will be filtered out in next step if
          condition is Required/Not Empty
        </Typography> */}
      </Box>
    </Paper>
  );
};

export default PreviewStep;